import { registerLocaleData } from '@angular/common';
import { Component, DestroyRef, HostBinding, inject, isDevMode, OnInit, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateAdapter } from '@angular/material/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import utc from 'dayjs/plugin/utc';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, first, map, sampleTime, skip, switchMap } from 'rxjs/operators';

import { BrowserStorageTypeEnum, BrowserStorageValue, PublicRoute, UserLanguage } from './interfaces';
import { AuthService } from './services/auth';
import { BeaconService } from './services/beacon';
import { BrowserStorageService } from './services/browser-storage';
import { ErrorService } from './services/error';
import { LanguageService } from './services/language';
import { MaintenanceService } from './services/maintenance';
import { MetaInfoService } from './services/meta-info';
import { TrackingService } from './services/tracking';
import { AppNotice } from './shared/app-notice/app-notice';
import { CampaignUser } from './shared/campaign-user/campaign-user';
import { FeedbackForm } from './shared/feedback-form/feedback-form';
import { MaintenanceModal } from './shared/maintenance-modal';
import { ModalForRoot } from './shared/modal/modal-for-root';
import { getNoticeModalVariant, NoticeModal } from './shared/notice-modal';

import { APP_NOTICE_EXCLUDED_PATHS, CURRENCY_LOCALE_FILE_PATH } from 'app/constants';
import { NewUserFactory as UserFactory } from 'app/factories/user_new/user.factory';
import { CampaignGraphQlType, NoticeModal as NoticeModalData } from 'app/graphql/generated/types-user';
import { environment } from 'environments/environment';

dayjs.extend(isBetween);
dayjs.extend(quarterOfYear);
dayjs.extend(localeData);
dayjs.extend(isoWeek);
dayjs.extend(utc);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(localizedFormat);

function loadLocale(localeId: string) {
    switch (localeId) {
        case 'et':
            return import('@angular/common/locales/et');
        case 'lv':
            return import('@angular/common/locales/lv');
        case 'lt':
            return import('@angular/common/locales/lt');
        case 'ru':
            return import('@angular/common/locales/ru');
        default:
            return import('@angular/common/locales/en');
    }
}

function loadDayjsLocale(localeId: string) {
    switch (localeId.split('-')[0]) {
        case 'et':
            return import('dayjs/locale/et');
        case 'lv':
            return import('dayjs/locale/lv');
        case 'lt':
            return import('dayjs/locale/lt');
        case 'ru':
            return import('dayjs/locale/ru');
        default:
            return Promise.resolve();
    }
}

interface NoticeAndCampaignsResult {
    notice: NoticeModalData | null;
    campaigns: CampaignGraphQlType[];
}

@Component({
    selector: 'app-root', //eslint-disable-line @angular-eslint/component-selector
    templateUrl: './app.html',
    styleUrl: './app.scss',
    imports: [RouterOutlet, FeedbackForm, ModalForRoot, CampaignUser, AppNotice, MaintenanceModal, NoticeModal],
})
export class App implements OnInit {
    private authService = inject(AuthService);
    private languageService = inject(LanguageService);
    private router = inject(Router);
    private metaInfoService = inject(MetaInfoService);
    private trackingService = inject(TrackingService);
    private browserStorageService = inject(BrowserStorageService);
    private translateService = inject(TranslateService);
    private beaconService = inject(BeaconService);
    private gaService = inject(GoogleAnalyticsService);
    private adapter = inject<DateAdapter<any>>(DateAdapter);
    private userFactory = inject(UserFactory);
    private errorService = inject(ErrorService);
    private renderer = inject(Renderer2);
    private destroyRef = inject(DestroyRef);
    private maintenanceService = inject(MaintenanceService);

    readonly isMaintenanceMode = this.maintenanceService.isMaintenanceMode;

    @HostBinding('class') cssClass = 'ready';

    showUserModal = false;
    appModalVisible = false;
    testEnvironmentName: string | null = null;
    userLanguage: UserLanguage;
    campaign: CampaignGraphQlType = null;
    notice: NoticeModalData | null = null;

    constructor() {
        const localeId = CURRENCY_LOCALE_FILE_PATH;

        loadLocale(localeId).then((module) => registerLocaleData(module?.default));

        this.translateService.onLangChange.subscribe((event: any) => {
            loadDayjsLocale(event.lang).then(() => {
                dayjs.locale(event.lang);
                this.adapter.setLocale(event.lang);
            });
        });
    }

    ngOnInit() {
        if (!isDevMode()) {
            this.subscribeGoogleAnalyticsPageChange();
            this.sendUserNavigationEventsToBeacon();
        }

        this.scrollToTopOnPathChange();

        this.checkWatermark();

        this.languageService.populateLanguagesForTranslateService();

        this.metaInfoService.init();

        setTimeout(() => {
            this.showAppModal();
        }, 2 * 1000);

        if (this.translateService.getCurrentLang()) {
            this.userLanguage = this.translateService.getCurrentLang() as UserLanguage;
        }

        this.authService
            .isLoggedIn()
            .pipe(
                distinctUntilChanged(),
                switchMap((status) => {
                    if (!status) return of<NoticeAndCampaignsResult>({ notice: null, campaigns: [] });

                    return this.userFactory.getNoticeModal().pipe(
                        first(),
                        catchError((error) => {
                            this.errorService.handleError(error);
                            return of<{ data: { noticeModal: NoticeModalData | null } | null }>({ data: null });
                        }),
                        switchMap((noticeResult) => {
                            const notice = noticeResult?.data?.noticeModal ?? null;

                            // Notice modal takes priority over the campaign modal
                            if (notice && getNoticeModalVariant(notice)) {
                                return of<NoticeAndCampaignsResult>({ notice, campaigns: [] });
                            }

                            return this.userFactory.getActiveCampaigns().pipe(
                                first(),
                                map(({ data }) => ({ notice: null, campaigns: data ?? [] })),
                                catchError((error) => {
                                    this.errorService.handleError(error);
                                    return of<NoticeAndCampaignsResult>({ notice: null, campaigns: [] });
                                }),
                                map(({ campaigns }) => ({ notice: null, campaigns: campaigns ?? [] })),
                            );
                        }),
                    );
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: ({ notice, campaigns }) => {
                    if (notice) {
                        this.notice = notice;
                        return;
                    }

                    if (campaigns.length) {
                        this.campaign = campaigns[0];
                        this.showCampaignUserModal();
                    }
                },
                error: (errors) => this.errorService.handleError(errors),
            });

        this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.userLanguage = this.translateService.getCurrentLang() as UserLanguage;
        });
    }

    checkWatermark(): void {
        const watermarkValue = environment.envWatermark;

        if (watermarkValue && watermarkValue !== '') {
            this.testEnvironmentName = watermarkValue;

            const repeatedText = Array(15).fill(` · ${watermarkValue}`).join('');
            this.renderer.setProperty(document.documentElement, 'style', `--repeated-env-text: "${repeatedText}";`);
        }
    }

    closeUserModal(): void {
        this.showUserModal = false;
    }

    closeAppModal(): void {
        this.appModalVisible = false;
    }

    getCampaignKey(): string {
        return 'campaign_' + this.campaign.id;
    }

    hasSeen(): boolean {
        return !!this.browserStorageService.getItem(this.getCampaignKey(), BrowserStorageTypeEnum.LocalStorage);
    }

    markAsSeen(): void {
        const value: BrowserStorageValue = {
            expiresAt: dayjs(this.campaign.endAt).endOf('day').unix(),
        };

        this.browserStorageService.setItem(this.getCampaignKey(), value, BrowserStorageTypeEnum.LocalStorage);
    }

    private gaTrackerAllowedResubCb(isAllowed: boolean): void {
        if (isAllowed) {
            this.router.events.subscribe((event) => {
                if (event instanceof NavigationEnd) {
                    this.gaService.pageView(event.urlAfterRedirects);
                }
            });
        }
    }

    private showAppModal() {
        const isExcluded = APP_NOTICE_EXCLUDED_PATHS.some((path) => window.location.href.includes(path));

        this.appModalVisible =
            /iPhone|Android/.test(navigator.userAgent) && !isExcluded && !/isMapOpen=true/.test(window.location.href);
    }

    private showCampaignUserModal() {
        if (!this.campaign) return;

        const externalRoutes = [PublicRoute.EXTERNAL_PURCHASE, PublicRoute.EXTERNAL_PURCHASE_AUTH];
        const isExternalRoute = externalRoutes.some((route) => this.router.url.startsWith(`/${route}`));

        if (isExternalRoute) return;

        const country = this.authService.getLoggedInUserCountry();
        if (
            !this.hasSeen() &&
            this.isUserCampaignActive(this.campaign.startAt, this.campaign.endAt) &&
            (this.userLanguage === 'et-ee' || this.userLanguage === 'ru-ru' || this.userLanguage === 'en-us') &&
            country?.abbreviation?.toUpperCase() === 'EST'
        ) {
            this.showUserModal = true;
            this.markAsSeen();
        }
    }

    private isUserCampaignActive(startAt: string, endAt: string) {
        const today = dayjs();
        return today.isBetween(dayjs(startAt), dayjs(endAt), 'day', '[]');
    }

    private subscribeGoogleAnalyticsPageChange(): void {
        this.trackingService
            .isTrackingAllowed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((isAllowed) => {
                this.gaTrackerAllowedResubCb(isAllowed);
            });
    }

    private scrollToTopOnPathChange(): void {
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                map((event) => event.urlAfterRedirects.split(/[?#]/)[0]),
                distinctUntilChanged(),
                skip(1),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => window.scrollTo({ top: 0 }));
    }

    private sendUserNavigationEventsToBeacon() {
        this.router.events
            .pipe(
                filter((result) => result instanceof NavigationEnd),
                sampleTime(200),
            )
            .subscribe(() => this.beaconService.event());
    }
}
